import React, { memo } from 'react';
import type { CSSProperties } from 'react';
import type { ToggleMark } from '@segmently/schema-models/v2';
import { mapFrameLayoutStyles } from '../../../utils/styleMapper';
import styles from './CheckBox.module.css';

export interface CheckBoxProps {
  toggleMark: ToggleMark | undefined;
  className?: string;
  'data-testid'?: string;
}

function parseHexColor(color: string): [number, number, number] | null {
  const match = color.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i);
  if (!match) return null;

  const hex = match[1].length === 3
    ? match[1].split('').map((part) => `${part}${part}`).join('')
    : match[1].slice(0, 6);
  return [
    Number.parseInt(hex.slice(0, 2), 16),
    Number.parseInt(hex.slice(2, 4), 16),
    Number.parseInt(hex.slice(4, 6), 16),
  ];
}

/** Pick the WCAG black/white foreground with the stronger contrast. */
function getContrastingForeground(background: string): string {
  const rgb = parseHexColor(background);
  if (!rgb) return '#ffffff';

  const linear = rgb.map((channel) => {
    const value = channel / 255;
    return value <= 0.04045
      ? value / 12.92
      : ((value + 0.055) / 1.055) ** 2.4;
  });
  const luminance = 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
  return luminance > 0.179 ? '#000000' : '#ffffff';
}

export const CheckBox = memo(function CheckBox({
  toggleMark,
  className,
  'data-testid': testId,
}: CheckBoxProps): React.ReactElement | null {
  if (!toggleMark) {
    return null;
  }

  const { checked, appearance, selectedAppearance, layout, kind } = toggleMark;

  const layoutStyles: CSSProperties = mapFrameLayoutStyles(layout);

  const activeStyle = checked ? selectedAppearance : appearance;
  const activeColor = activeStyle?.color;
  const shouldFillBackground = checked && selectedAppearance?.isBackgroundFilled;

  const checkboxStyle: CSSProperties = {
    ...(activeColor && { borderColor: activeColor }),
    ...(shouldFillBackground && activeColor && { backgroundColor: activeColor }),
    // Dynamic sizing from v2 dimensions (v1 hardcoded 24x24 in CSS)
    ...(activeStyle?.dimensions?.width !== undefined && { width: activeStyle.dimensions.width }),
    ...(activeStyle?.dimensions?.height !== undefined && { height: activeStyle.dimensions.height }),
  };

  const checkmarkColor = shouldFillBackground && activeColor
    ? getContrastingForeground(activeColor)
    : selectedAppearance?.color || activeColor;
  const isCircle = kind === 'circle';

  const checkboxClassName = [
    styles.checkBox,
    checked && styles.checked,
    isCircle && styles.circle,
    className,
  ].filter(Boolean).join(' ');

  const hasLayoutStyles = Object.keys(layoutStyles).length > 0;

  const checkboxElement = (
    <div
      className={checkboxClassName}
      style={checkboxStyle}
      data-testid={testId}
      data-checked={checked ? 'true' : 'false'}
      role="checkbox"
      aria-checked={checked}
    >
      {checked && (
        <span
          className={styles.checkmark}
          style={checkmarkColor ? { color: checkmarkColor } : undefined}
        >
          ✓
        </span>
      )}
    </div>
  );

  if (hasLayoutStyles) {
    return <div style={layoutStyles}>{checkboxElement}</div>;
  }

  return checkboxElement;
});

CheckBox.displayName = 'CheckBox';
